import { useCallback, useEffect, useRef, useState } from 'react';

const SpeechRecognitionImpl =
  typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

/**
 * Uses the browser's built-in speech recognition (Web Speech API). Free and needs
 * no backend, but only works on the default microphone input in Chromium browsers.
 */
export function useBrowserSpeechRecognition({ onTranscript }) {
  const [status, setStatus] = useState('idle'); // idle | recording | error
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const shouldRunRef = useRef(false);

  const isSupported = Boolean(SpeechRecognitionImpl);

  const start = useCallback(() => {
    if (!isSupported) {
      setError('Your browser does not support built-in speech recognition (try Chrome or Edge).');
      setStatus('error');
      return;
    }
    setError(null);
    shouldRunRef.current = true;

    const recognition = new SpeechRecognitionImpl();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = navigator.language || 'en-US';

    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          const text = result[0].transcript.trim();
          if (text) onTranscript?.(text);
        }
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      console.error('Speech recognition error', event.error);
      shouldRunRef.current = false;
      setStatus('error');
      setError(`Speech recognition error: ${event.error}`);
    };

    // the API auto-stops after silence; restart it to keep "continuous" recognition going
    recognition.onend = () => {
      if (shouldRunRef.current) {
        try {
          recognition.start();
        } catch {
          // ignore restart races when already starting
        }
      } else {
        setStatus('idle');
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setStatus('recording');
  }, [isSupported, onTranscript]);

  const stop = useCallback(() => {
    shouldRunRef.current = false;
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setStatus('idle');
  }, []);

  useEffect(() => () => {
    shouldRunRef.current = false;
    recognitionRef.current?.stop();
  }, []);

  return { start, stop, status, error, isSupported };
}
